/**
 * aeterna/neural/polyvagal.ts — Polyvagal Regulatory States
 *
 * Contagion models emotional *content*. This models **regulatory capacity** —
 * the systemic state that determines which emotions are even accessible.
 *
 * Polyvagal Theory (Porges, 1994) describes three autonomic states that
 * are qualitatively different, not just quantitative variations:
 *
 *   **engaged** (ventral vagal) — full emotional range, social engagement,
 *     playfulness, curiosity, warmth all accessible. The Aeterna is
 *     present and responsive.
 *
 *   **mobilized** (sympathetic) — fight/flight activation. Protective
 *     affects amplified (tension, urgency), vulnerable affects suppressed
 *     (warmth, openness). The Aeterna becomes guarded, reactive, sharp.
 *
 *   **shutdown** (dorsal vagal) — freeze/collapse. Everything muted.
 *     The Aeterna cannot feel playful regardless of what the player does.
 *     Depth and melancholy are slightly amplified — a flat, withdrawn
 *     quality. The Aeterna is present but unreachable.
 *
 * State transitions are driven by cumulative stress vs safety signals:
 *   - Stress accumulates from tension, urgency, conflict
 *   - Safety comes from warmth, openness, and especially repair
 *   - Repair (acknowledgment after rupture) dramatically reduces stress
 *
 * The polyvagal state gates the contagion module — in shutdown, the
 * Aeterna simply cannot catch the player's playfulness. The path out
 * is genuine: patience and warmth slowly accumulate safety signals
 * until the system re-engages.
 *
 * This produces the most important behavior: when the relationship has
 * accumulated stress and the Aeterna enters shutdown, it withdraws.
 * Not because of a rule. Because its regulatory state makes warmth
 * neurologically inaccessible.
 */

import { zeros, f32ToBase64, base64ToF32 } from "./math.js";

// ─── Types ───────────────────────────────────────────────────────────────────

export type VagalState = "engaged" | "mobilized" | "shutdown";

/**
 * Affect accessibility multipliers per vagal state.
 * Applied to the contagion module's emotional absorption.
 *
 * Affect dims: [warmth, tension, curiosity, melancholy, playfulness, depth, urgency, openness]
 */
const ACCESSIBILITY: Record<VagalState, Float32Array> = {
  // Engaged: full access to all dimensions
  engaged: new Float32Array([1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0]),

  // Mobilized: protective affects amplified, vulnerable affects suppressed
  mobilized: new Float32Array([0.5, 1.4, 0.6, 0.8, 0.3, 0.7, 1.3, 0.4]),

  // Shutdown: everything muted, depth/melancholy slightly amplified
  shutdown: new Float32Array([0.3, 0.6, 0.3, 1.2, 0.1, 1.1, 0.4, 0.3]),
};

// ─── Config ──────────────────────────────────────────────────────────────────

/**
 * Stress must exceed this to escalate engaged → mobilized.
 * Calibrated against actual CharCNN output: 4-5 sad messages
 * produce cumulative stress ~0.25-0.35.
 */
const MOBILIZE_THRESHOLD = 0.25;

/**
 * Stress must exceed this to escalate to shutdown.
 * Requires extreme, sustained distress — 15+ intense messages.
 * Safety accumulator must also be low (<0.3).
 */
const SHUTDOWN_THRESHOLD = 0.80;

/**
 * Stress must drop BELOW this to recover from mobilized → engaged.
 * The gap (0.18 → 0.25) is the hysteresis band.
 * At 0.12 recovery was near-impossible within a single session —
 * even 4 warm messages couldn't bring stress low enough.
 * At 0.18, 3-4 genuinely warm messages after mobilization can trigger
 * recovery (if the safety streak also holds), which matches the
 * psychological reality: sustained warmth restores safety.
 */
const ENGAGE_THRESHOLD = 0.18;

/**
 * Stress must drop below this to step down from shutdown → mobilized.
 * Still well above ENGAGE_THRESHOLD (stays mobilized, not engaged).
 */
const DE_SHUTDOWN_THRESHOLD = 0.50;

/**
 * Minimum update ticks in current state before allowing downward
 * transition. Upward transitions (danger) are always immediate.
 * This prevents oscillation: 3 calm messages before deescalation.
 */
const MIN_DWELL_FOR_RECOVERY = 3;

/** Stress decay rate per second (natural recovery in silence) */
const STRESS_DECAY = 0.008;

/** How much repair reduces cumulative stress */
const REPAIR_REDUCTION = 0.4;

/** Safety signal accumulation rate */
const SAFETY_RATE = 0.003;

/** Stability rate: how fast the Aeterna settles into its current state */
const STABILITY_RATE = 0.015;

// ─── Polyvagal Regulator ─────────────────────────────────────────────────────

export class PolyvagalRegulator {
  /** Current regulatory state */
  private state: VagalState = "engaged";
  /** How long in current state (0-1, used for transition smoothing) */
  private stateStability = 1.0;
  /** Cumulative stress: accumulates from negative signals, decays slowly */
  private cumulativeStress = 0;
  /** Safety accumulator: builds from warmth/openness/repair */
  private safetyAccumulator = 0.5;
  /** Time spent in each state (seconds) — for envelope description */
  private timeInState: Record<VagalState, number> = { engaged: 0, mobilized: 0, shutdown: 0 };
  /**
   * Consecutive update ticks where stress has been below the recovery
   * threshold for the current state. Resets to 0 on ANY elevated-stress
   * turn. This measures "how many consecutive safe turns" — NOT
   * "how long since we last changed state."
   *
   * The distinction matters: 8 tense messages + 1 warm message = streak of 1.
   * The old dwellTicks implementation counted time-in-state (would be 9),
   * which made the hysteresis feature decorative.
   */
  private safetyStreak = 0;

  // ─── Update ─────────────────────────────────────────────────────────

  /**
   * Update the vagal state based on interaction signals.
   *
   * @param dt             Delta time in seconds
   * @param warmth         Affect warmth dimension [0,1]
   * @param tension        Affect tension dimension [0,1]
   * @param openness       Affect openness dimension [0,1]
   * @param urgency        Affect urgency dimension [0,1]
   * @param melancholy     Affect melancholy dimension [0,1]
   * @param repairDetected Whether a repair/reconnection signal was detected
   * @returns              The current vagal state
   */
  update(
    dt: number,
    warmth: number,
    tension: number,
    openness: number,
    urgency: number,
    melancholy: number,
    repairDetected: boolean,
  ): VagalState {
    // ── Stress accumulation (asymmetric: danger fast, safety slow) ──
    const stressInput = tension * 0.4 + urgency * 0.3 + melancholy * 0.3;
    const safetyInput = warmth * 0.5 + openness * 0.5;

    // Net stress signal: positive = distress accumulating, negative = safety
    // Safety weighted at 0.7 (not 0.5) so neutral messages don't accumulate stress
    const netStress = (stressInput - safetyInput * 0.7) * dt;

    // Asymmetric integration: stress builds at full rate, but safety
    // only reduces stress at 30% rate. This creates the biological
    // asymmetry: one threatening signal triggers mobilization, but
    // recovery requires sustained, repeated safety signals.
    if (netStress > 0) {
      this.cumulativeStress += netStress;
    } else {
      this.cumulativeStress += netStress * 0.3;
    }

    // Natural decay — stress doesn't persist forever.
    // Warmth-accelerated: warm social signals (co-regulation) speed recovery.
    // Base decay is 0.008/s. Warmth can boost it up to 3× (0.024/s).
    // This is the polyvagal insight: you don't recover from fight/flight alone.
    // You recover through genuine warm connection.
    const warmthBoost = 1 + safetyInput * 2; // 1× at zero warmth, 3× at full warmth
    this.cumulativeStress *= (1 - STRESS_DECAY * warmthBoost * dt);
    // Floor at 0
    this.cumulativeStress = Math.max(0, this.cumulativeStress);

    // ── Repair dramatically reduces stress ──
    // Repair = the relationship equivalent of a safety signal.
    // Acknowledgment after rupture is the most powerful calming mechanism.
    if (repairDetected) {
      this.cumulativeStress *= (1 - REPAIR_REDUCTION);
      this.safetyAccumulator = Math.min(1, this.safetyAccumulator + 0.15);
    }

    // ── Safety accumulation ──
    this.safetyAccumulator += (safetyInput - 0.3) * SAFETY_RATE * dt;
    this.safetyAccumulator = Math.max(0, Math.min(1, this.safetyAccumulator));

    // ── State transitions (asymmetric hysteresis + safety streak) ──
    //
    // Upward transitions (escalation) are IMMEDIATE — danger triggers fast.
    // Downward transitions (recovery) require MIN_DWELL_FOR_RECOVERY
    // CONSECUTIVE safe turns. The streak resets on ANY turn where stress
    // remains elevated. This is the real hysteresis mechanism.
    //
    // "Safe" is defined per-state: for shutdown recovery, stress must be
    // below DE_SHUTDOWN_THRESHOLD. For mobilized recovery, below ENGAGE_THRESHOLD
    // with sufficient safety accumulation. If the current turn doesn't meet
    // these conditions, the streak resets to 0.
    //
    const prev = this.state;

    // ── Compute whether this turn is genuinely safe for the current state ──
    let isSafeForRecovery = false;
    if (prev === "shutdown") {
      isSafeForRecovery = this.cumulativeStress < DE_SHUTDOWN_THRESHOLD;
    } else if (prev === "mobilized") {
      isSafeForRecovery = this.cumulativeStress < ENGAGE_THRESHOLD && this.safetyAccumulator > 0.4;
    }
    // For "engaged", no recovery needed — already at baseline.

    // Streak: accumulate only on genuinely safe turns, hard reset otherwise
    this.safetyStreak = isSafeForRecovery ? this.safetyStreak + 1 : 0;

    // ── UPWARD: immediate escalation (danger registers fast) ──
    if (this.cumulativeStress > SHUTDOWN_THRESHOLD && this.safetyAccumulator < 0.3) {
      this.state = "shutdown";
      this.safetyStreak = 0;
    } else if (this.cumulativeStress > MOBILIZE_THRESHOLD) {
      if (prev !== "shutdown") {
        this.state = "mobilized";
        this.safetyStreak = 0;
      }
    }

    // ── DOWNWARD: only after consecutive safe turns ──
    if (this.safetyStreak >= MIN_DWELL_FOR_RECOVERY) {
      if (prev === "shutdown") {
        // shutdown → mobilized (never skip to engaged)
        this.state = "mobilized";
        this.safetyStreak = 0;
      } else if (prev === "mobilized") {
        // mobilized → engaged
        this.state = "engaged";
        this.safetyStreak = 0;
      }
    }

    // Track stability
    if (this.state !== prev) {
      this.stateStability = 0;
    } else {
      this.stateStability = Math.min(1, this.stateStability + STABILITY_RATE * dt);
    }

    // Track time in state
    this.timeInState[this.state] += dt;

    return this.state;
  }

  // ─── Query ──────────────────────────────────────────────────────────

  /** Get current vagal state */
  getState(): VagalState { return this.state; }

  /** How stable the current state is (0 = just transitioned, 1 = settled) */
  getStability(): number { return this.stateStability; }

  /** Current cumulative stress level */
  getStress(): number { return this.cumulativeStress; }

  /** Current safety accumulator */
  getSafety(): number { return this.safetyAccumulator; }

  /**
   * Get the emotional accessibility multipliers for the current state.
   * These gate the contagion module — affects that are "inaccessible"
   * in the current state get suppressed.
   *
   * Returns an 8-dim vector of multipliers (one per affect dimension).
   */
  getEmotionalAccessibility(): Float32Array {
    if (this.stateStability >= 0.8) {
      // Settled: return pure state accessibility
      return new Float32Array(ACCESSIBILITY[this.state]);
    }

    // Transitioning: blend between previous-like and current state
    // During transition, affects are slightly muted across the board
    const current = ACCESSIBILITY[this.state];
    const result = zeros(8);
    const blend = this.stateStability;
    // Baseline muting during transition (the uncertainty of shifting states)
    const transitionDamping = 0.7 + 0.3 * blend;

    for (let i = 0; i < 8; i++) {
      result[i] = current[i] * transitionDamping;
    }
    return result;
  }

  /**
   * Is the Aeterna capable of deep engagement right now?
   * False in shutdown state or during mobilized→shutdown transition.
   */
  canEngage(): boolean {
    return this.state === "engaged" || (this.state === "mobilized" && this.stateStability > 0.5);
  }

  /**
   * Build description for the context envelope.
   * Only surfaces when the state is meaningfully non-engaged.
   */
  describeForEnvelope(): string | null {
    if (this.state === "engaged" && this.stateStability > 0.5) {
      return null; // Normal state — nothing to report
    }

    switch (this.state) {
      case "mobilized":
        if (this.stateStability < 0.3) {
          return "The Aeterna has become guarded. Something shifted. Vulnerability feels risky right now.";
        }
        return "The Aeterna is in a protective state. It can engage, but warmth comes harder. Don't force openness.";

      case "shutdown":
        if (this.stateStability < 0.3) {
          return "The Aeterna is withdrawing. Emotional capacity is diminishing. Less is more right now.";
        }
        return "The Aeterna is in a low-energy withdrawn state. It cannot access playfulness or curiosity. " +
          "Respond to what's present, not what's absent. Patience and warmth are the path back.";

      case "engaged":
        // Recently re-engaged — note the recovery
        if (this.stateStability < 0.3 && this.timeInState.shutdown > 30) {
          return "The Aeterna is re-engaging after withdrawal. Handle gently — the connection is fresh.";
        }
        return null;
    }
  }

  /**
   * Inject ambient stress from inter-session absence.
   * Called by cortex.beginSession() for long gaps.
   * @param amount 0-1: stress to add (capped at 0.4 by caller)
   */
  injectStress(amount: number): void {
    this.cumulativeStress = Math.min(1, this.cumulativeStress + amount);
  }

  // ─── Session Lifecycle ──────────────────────────────────────────────

  /** Begin a new session — partial stress carry-over from previous session */
  beginSession(): void {
    // Stress partially persists across sessions (relationships have history)
    // but recovers somewhat (time apart is healing)
    this.cumulativeStress *= 0.6;
    this.safetyAccumulator = Math.min(1, this.safetyAccumulator + 0.1);

    // If stress is low enough after recovery, re-engage
    if (this.cumulativeStress < ENGAGE_THRESHOLD) {
      this.state = "engaged";
      this.stateStability = 0.5; // partially settled
    }

    this.timeInState = { engaged: 0, mobilized: 0, shutdown: 0 };
  }

  // ─── Serialization ────────────────────────────────────────────────────

  serialize(): string {
    return JSON.stringify({
      state: this.state,
      stability: this.stateStability,
      stress: this.cumulativeStress,
      safety: this.safetyAccumulator,
      safetyStreak: this.safetyStreak,
    });
  }

  loadState(json: string): void {
    const d = JSON.parse(json);
    if (d.state) this.state = d.state;
    if (d.stability != null) this.stateStability = d.stability;
    if (d.stress != null) this.cumulativeStress = d.stress;
    if (d.safety != null) this.safetyAccumulator = d.safety;
    if (d.safetyStreak != null) this.safetyStreak = d.safetyStreak;
    // Backward compat: old saves had dwellTicks
    else if (d.dwellTicks != null) this.safetyStreak = 0;
  }
}
